/**
 * "Report an issue" (bimgo-ai-web#156) — opened from the bottom of the sidebar.
 * Signed-in only: the ticket goes to api-v2 with the user's
 * token, and the sidebar is the part of the shell that has one (the
 * titlebar's Help group is reachable from the login page).
 *
 * Title, description and at most one screenshot are the user's; the logs are the
 * desktop's (`diagnostics.ticketLogs`, bridge v9), attached without a note in
 * the dialog. The logic is `lib/tickets.ts`.
 *
 * The draft outlives closing the dialog — the component stays mounted in the
 * shell — and is cleared only once a report is filed.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { bearer } from "../lib/accessToken";
import { apiFetch } from "../lib/apiFetch";
import { API_BASE_URL } from "../lib/config";
import {
  TICKET_DESCRIPTION_MAX_LENGTH,
  TICKET_SCREENSHOT_EXTENSIONS,
  TICKET_SCREENSHOT_MAX_MB,
  TICKET_TITLE_MAX_LENGTH,
  collectTicketLogs,
  detectTicketLogs,
  submitTicket,
  validateScreenshot,
} from "../lib/tickets";
import { Modal } from "./settings/Modal";
import { Toast } from "./Toast";

/** `{ desktop_version, environment }` from the bridge — whatever of it answers. */
async function readClientInfo(): Promise<Record<string, string>> {
  try {
    const info = await window.bimgo?.app?.info?.();
    const clientInfo: Record<string, string> = {};
    if (info?.version) clientInfo["desktop_version"] = info.version;
    if (info?.environment) clientInfo["environment"] = info.environment;
    return clientInfo;
  } catch {
    return {};
  }
}

function formatSize(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function ReportIssueDialog({
  open,
  onClose,
  token,
  orgId,
}: {
  open: boolean;
  onClose: () => void;
  token: string | null;
  orgId: string | null;
}) {
  const { t } = useLocale();
  const fileInput = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState<{ tone: "ok" | "err"; text: string } | null>(null);

  // Confirmations clear themselves; an error stays until it is dismissed, since it may need reading.
  useEffect(() => {
    if (toast?.tone !== "ok") return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const canSubmit = !!token && title.trim() !== "" && description.trim() !== "" && !submitting;

  const pickScreenshot = (file: File | undefined) => {
    if (!file) return;
    const error = validateScreenshot(file);
    if (error) {
      setToast({
        tone: "err",
        text: t(`ticket.fileError.${error}`, { file: file.name, limit: TICKET_SCREENSHOT_MAX_MB }),
      });
      return;
    }
    setScreenshot(file);
  };

  const submit = async () => {
    if (!canSubmit || !token) return;
    setSubmitting(true);
    try {
      const [logs, clientInfo] = await Promise.all([
        collectTicketLogs(detectTicketLogs(window)),
        readClientInfo(),
      ]);
      const ticket = await submitTicket(
        {
          title,
          description,
          screenshot,
          logs,
          organizationId: orgId,
          pageUrl: window.location.origin + window.location.pathname,
          clientInfo,
        },
        { fetchImpl: apiFetch, apiBaseUrl: API_BASE_URL(), authorization: bearer(token) },
      );
      setTitle("");
      setDescription("");
      setScreenshot(null);
      onClose();
      setToast({ tone: "ok", text: t("ticket.submitted", { number: ticket.issue_number }) });
    } catch (error) {
      setToast({
        tone: "err",
        text: t("ticket.submitFailed", { message: error instanceof Error ? error.message : String(error) }),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        closable={!submitting}
        title={t("ticket.title")}
        description={t("ticket.subtitle")}
        footer={
          <>
            <button type="button" className="settings-btn secondary" onClick={onClose} disabled={submitting}>
              {t("common.cancel")}
            </button>
            <button type="button" className="settings-btn" onClick={() => void submit()} disabled={!canSubmit}>
              {submitting ? t("ticket.submitting") : t("ticket.submit")}
            </button>
          </>
        }
      >
        <div className="settings-field">
          <label className="auth-label" htmlFor="ticket-title">
            {t("ticket.titleLabel")}
            <span className="param-required" aria-hidden="true">*</span>
          </label>
          <input
            id="ticket-title"
            className="settings-input"
            required
            value={title}
            maxLength={TICKET_TITLE_MAX_LENGTH}
            placeholder={t("ticket.titlePlaceholder")}
            onChange={(e) => setTitle(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="settings-field">
          <label className="auth-label" htmlFor="ticket-description">
            {t("ticket.descriptionLabel")}
            <span className="param-required" aria-hidden="true">*</span>
          </label>
          <textarea
            id="ticket-description"
            className="settings-input settings-textarea"
            required
            rows={6}
            value={description}
            maxLength={TICKET_DESCRIPTION_MAX_LENGTH}
            placeholder={t("ticket.descriptionPlaceholder")}
            onChange={(e) => setDescription(e.target.value)}
            disabled={submitting}
          />
        </div>

        <div className="settings-field">
          <span className="auth-label">{t("ticket.screenshotLabel")}</span>
          <p className="settings-note">{t("ticket.screenshotHint", { limit: TICKET_SCREENSHOT_MAX_MB })}</p>
          {screenshot ? (
            <div className="ticket-screenshot">
              <span className="ticket-screenshot-name">{screenshot.name}</span>
              <span className="settings-note">{formatSize(screenshot.size)}</span>
              <button
                type="button"
                className="settings-btn secondary is-small"
                onClick={() => setScreenshot(null)}
                disabled={submitting}
              >
                {t("ticket.removeScreenshot")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="settings-btn secondary is-small"
              onClick={() => fileInput.current?.click()}
              disabled={submitting}
            >
              {t("ticket.chooseScreenshot")}
            </button>
          )}
          <input
            ref={fileInput}
            type="file"
            accept={TICKET_SCREENSHOT_EXTENSIONS.join(",")}
            hidden
            onChange={(e) => {
              pickScreenshot(e.target.files?.[0]);
              // Allow re-picking the same file after removing it.
              e.target.value = "";
            }}
          />
        </div>
      </Modal>

      {toast ? <Toast tone={toast.tone} text={toast.text} onClose={() => setToast(null)} /> : null}
    </>
  );
}
